import React from "react";
import PropTypes from "prop-types";
import i18n, { aliases } from "../../i18n/i18n";
import { AuthProvider, useAuth } from "react-oidc-context";
import Loader from "../ui/loader";
import { useHistory } from "react-router-dom";
import { fetcher } from "@weezevent/weezjs-core";

const FetchContext = React.createContext();

export const useFetch = () => React.useContext(FetchContext);

const AuthenticationInner = ({ children }) => {
	const auth = useAuth();

	React.useEffect(() => {
		if (!auth.user) {
			// No User
			return;
		}
		const userLocale =
			auth.user.profile.locale || auth.user.profile.data?.locale || "en";
		const accountsTokenLocale = userLocale.replace(/_/g, "-");
		const locale = aliases[accountsTokenLocale] ?? accountsTokenLocale;

		if (locale !== i18n.language) {
			i18n.changeLanguage(locale);
		}
		i18n.accountLocale = locale;
	}, [auth]);

	if (auth.isLoading || auth.error) {
		return <Loader active />;
	}

	if (auth.isAuthenticated) {
		// Create a fetch alias that injects the token
		const fetch = (url, opts) => {
			opts = opts || {};
			const headers = (opts.headers = opts.headers || {});
			const tokenType = "Bearer"; // Should be auth.user.token_type, but accounts smh...
			headers.Authorization = `${tokenType} ${auth.user.access_token}`;
			return window.fetch(url, opts);
		};

		fetcher.setToken(auth.user.access_token);
		// Inject a fetch command in a context
		return (
			<FetchContext.Provider value={fetch}>
				{children}
			</FetchContext.Provider>
		);
	}
	return <SigninRedirect />;
};

AuthenticationInner.propTypes = {
	children: PropTypes.node.isRequired
};

const SigninRedirect = () => {
	const auth = useAuth();
	React.useEffect(() => {
		auth.signinRedirect({
			url_state: String(window.location.pathname)
		});
	}, [auth]);
	return <Loader active />;
};

export const Authentication = ({ children, config }) => {
	const history = useHistory();

	const onSigninCallback = user => {
		history.push(
			{ pathname: user.url_state || "/access" },
			document.title,
			window.location.pathname
		);
	};

	return (
		<AuthProvider
			automaticSilentRenew
			authority={config.OIDCUrl}
			redirect_uri={`${window.location.origin}/access/`}
			onSigninCallback={onSigninCallback}
			client_id={config.clientId}
		>
			<AuthenticationInner>{children}</AuthenticationInner>
		</AuthProvider>
	);
};

Authentication.propTypes = {
	children: PropTypes.node.isRequired,
	onToken: PropTypes.func,
	config: PropTypes.shape({
		OIDCUrl: PropTypes.string,
		clientId: PropTypes.string
	}).isRequired
};
